import { useMemo } from 'react';

/**
 * Hook to determine other user info and loading state based on user type and conversation type
 * @param {Object} params - Parameters
 * @param {boolean} params.isEmployee - Whether current user is an employee
 * @param {string} params.conversationType - Type of conversation (applicant, talent, etc.)
 * @param {Object} params.jobOffer - Job offer data
 * @param {boolean} params.jobLoading - Job offer loading state
 * @param {Object} params.restaurantInfo - Restaurant info data
 * @param {boolean} params.restaurantLoading - Restaurant loading state
 * @param {Object} params.employeeInfo - Employee info data
 * @param {boolean} params.employeeLoading - Employee loading state
 * @param {boolean} params.conversationLoading - Conversation loading state
 * @param {string} params.restaurantUserId - Restaurant user ID
 * @param {string} params.employeeId - Employee ID
 * @returns {Object} otherUserInfo and otherUserLoading
 */
const useOtherUserInfo = ({
  isEmployee,
  conversationType,
  jobOffer,
  jobLoading,
  restaurantInfo,
  restaurantLoading,
  employeeInfo,
  employeeLoading,
  conversationLoading,
  restaurantUserId,
  employeeId
}) => {
  const isApplicantConversation = conversationType === 'applicant';
  const isTalentConversation = conversationType === 'talent';

  return useMemo(() => {
    let otherUserInfo, otherUserLoading;

    if (isEmployee) {
      // Employee user
      if (isApplicantConversation) {
        // Employee viewing applicant conversation -> show job info
        otherUserInfo = jobOffer;
        otherUserLoading = conversationLoading || jobLoading;
      } else if (isTalentConversation) {
        // Employee viewing talent conversation -> show restaurant info
        otherUserInfo = restaurantInfo;
        otherUserLoading = conversationLoading || restaurantLoading;
      } else {
        // Fallback: if we have restaurantUserId, show restaurant info
        if (restaurantUserId) {
          otherUserInfo = restaurantInfo;
          otherUserLoading = conversationLoading || restaurantLoading;
        } else {
          otherUserInfo = null;
          otherUserLoading = conversationLoading;
        }
      }
    } else {
      // Company user
      if (isApplicantConversation) {
        // Company viewing applicant conversation -> show employee info
        otherUserInfo = employeeInfo;
        otherUserLoading = employeeLoading;
      } else if (isTalentConversation) {
        // Company viewing talent conversation -> show employee info
        otherUserInfo = employeeInfo;
        otherUserLoading = employeeLoading;
      } else {
        // Fallback: if we have employeeId, show employee info
        if (employeeId) {
          otherUserInfo = employeeInfo;
          otherUserLoading = employeeLoading;
        } else {
          otherUserInfo = null;
          otherUserLoading = employeeLoading;
        }
      }
    }

    return { otherUserInfo, otherUserLoading };
  }, [
    isEmployee,
    isApplicantConversation,
    isTalentConversation,
    jobOffer,
    jobLoading,
    restaurantInfo,
    restaurantLoading,
    employeeInfo,
    employeeLoading,
    conversationLoading,
    restaurantUserId,
    employeeId
  ]);
};

export default useOtherUserInfo;

